import { useGetPayoutTransactionsAdminHttp } from "../hooks/http/admin/payouts/get-payout-transcations.admin.http";
import {
	CompletedBadge,
	PendingApprovalBadge,
	ProcessingBadge,
} from "./badges.components";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "./ui/accordion";
import { Separator } from "./ui/separator";
import { handleHttpError } from "../utils/utils";
import { useToast } from "../hooks/use-toast";
import {
	IPayoutType,
	ITransationStatus,
	type IPayoutTransactionMinimal,
} from "../types/models/payout-transaction.modals.types";
import { useEffect, useState } from "react";
import Spinner from "./spinner.components";
import { DateTime } from "luxon";
import type { IPagination } from "../types/models/pagination.models.types";
import PaginationComponent from "./pagination.components";
import type { ISocial } from "../types/models/social.models.types";

export default function UserTransaction({
	social,
}: {
	social: ISocial;
}) {
	const { execute: getPayoutRequestsHttp, fetching: fetchingPayoutRequests } =
		useGetPayoutTransactionsAdminHttp();

	const [payouts, setPayouts] = useState<IPayoutTransactionMinimal[]>([]);
	const [paginationData, setPaginationData] = useState<IPagination>();

	const { toast } = useToast();

	async function fetchPayouts(
		data: IPayoutTransactionMinimal[],
		pageNo: number,
	) {
		if (!social.creator?.id) {
			return;
		}

		const response = await getPayoutRequestsHttp({
			body: null,
			queryParams: { creator: social.creator.id, page: pageNo },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setPayouts([...data, ...response.data.transactions]);
		setPaginationData(response.data.pagination);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		fetchPayouts([], 1);
	}, [social.creator?.id]);

	if (payouts.length <= 0 && fetchingPayoutRequests) {
		return (
			<div className="flex flex-col justify-center items-center h-96">
				<Spinner width={20} />
			</div>
		);
	}

	return (
		<div className="flex flex-col px-2 w-full">
			<div className="flex justify-between items-center px-2 pt-5 w-full text-secondary">
				<p className="">Amount</p>
				<p className="pr-28">Status</p>
				<p>Date</p>
			</div>
			<div className="flex flex-col gap-5 pt-5 w-full">
				{payouts.map((payout) => {
					return (
						<Accordion
							className="bg-tertiarybuttonbg px-4 rounded-lg w-full"
							type="single"
							collapsible
							key={payout.id}
						>
							<AccordionItem value="followers">
								<AccordionTrigger className="flex flex-row gap-2">
									<div className="flex justify-between items-center w-full">
										{payout.transactionType === IPayoutType.CREDITED && (
											<p className="w-24 font-semibold text-primarybuttonbg text-start">
												+₹{payout.amount}
											</p>
										)}

										{payout.transactionType === IPayoutType.DEBITED && (
											<p className="w-24 font-semibold text-rejectbuttontext text-start">
												-₹{payout.amount}
											</p>
										)}
										<div className="flex items-start w-40">
											{payout.transactionStatus.status ===
												ITransationStatus.COMPLETED && <CompletedBadge />}
											{payout.transactionStatus.status ===
												ITransationStatus.PENDING_APPROVAL && (
												<PendingApprovalBadge />
											)}
											{payout.transactionStatus.status ===
												ITransationStatus.PROCESSING && <ProcessingBadge />}
										</div>

										<div className="flex flex-col text-end">
											<p className="font-semibold text-white text-xs">
												{DateTime.fromISO(
													payout.transactionStatus.timestamp.toString(),
												).toFormat("dd/MM/yyyy")}
											</p>
											<p className="font-semibold text-white text-xs">
												{DateTime.fromISO(
													payout.transactionStatus.timestamp.toString(),
												).toFormat("hh:mm a")}
											</p>
										</div>
									</div>
								</AccordionTrigger>
								<AccordionContent>
									<Separator />
									<div className="flex flex-col gap-5 pt-5">
										<div className="flex flex-col">
											<p className="font-semibold text-secondary text-xs">
												Transaction message
											</p>
											<p className="text-xl">
												{payout.transactionStatus.message
													? payout.transactionStatus.message
													: "-"}
											</p>
										</div>
										<div className="flex flex-col">
											<p className="font-semibold text-secondary text-xs">
												Transaction id
											</p>
											<p className="text-xl tracking-wider">
												{payout.cashfreeTransactionId
													? payout.cashfreeTransactionId
													: "-"}
											</p>
										</div>
									</div>
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					);
				})}
				{payouts.length === 0 && (
					<div className="flex justify-center items-center pt-20 w-full h-96 text-secondary">
						No transactions found
					</div>
				)}
			</div>
			{paginationData && (
				<PaginationComponent
					fetching={fetchingPayoutRequests}
					paginationData={paginationData}
					onClick={() => fetchPayouts(payouts, paginationData.page + 1)}
				/>
			)}
		</div>
	);
}
